import { Component, ElementRef, EventEmitter, OnInit, OnDestroy, Output, ViewChild, AfterViewInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { GadgetComplete } from 'node_js/models/business/lab/gadget-complete';
import { FileUploadComplete } from 'node_js/models/business/lab/file-upload-complete';
import { RoleEnum } from 'node_js/models/enums/role';
import { SnackBarColor } from 'node_js/models/enums/snackbar-color';
import { GadgetService } from 'src/app/services/gadget.service';
import { LabService } from 'src/app/services/lab.service';
import { UserAuthService } from 'src/app/services/user-auth.service';
import { presentAlertConfirm, presentLoading, presentSnackBar } from 'src/global';
import { Platform } from '@ionic/angular';
import { AndroidBackPriority } from 'node_js/models/enums/android-back-priority';
import Swiper from 'swiper';

@Component({
  selector: 'gadget-settings',
  templateUrl: './gadget-settings.component.html',
  styleUrls: ['./gadget-settings.component.scss'],
})
export class GadgetSettingsComponent implements OnInit, OnDestroy, AfterViewInit {

  @Output() exitGadgetSettings: EventEmitter<void> = new EventEmitter();

  canEdit = false;
  clonedGadgetComplete: GadgetComplete;
  // selectedOwnerUserId removed

  @ViewChild('mainSwiper') mainSwiperRef!: ElementRef;

  selectedCuratedImage: string | null = '../../assets/icon/default_gadget.png';

  // Back button subscription
  private backButtonSub: any;

  constructor(private labService: LabService, private gadgetService: GadgetService, private matDialog: MatDialog,
    private userAuthService: UserAuthService, private matSnackBar: MatSnackBar,
    private dialogRef: MatDialogRef<GadgetSettingsComponent>, private platform: Platform
  ) { }

  ngOnInit() {
    this.createClone();
    this.labService.currentSelectedGadgetSettingsChanged.subscribe(() => {
      this.createClone();
    })

    // Set up back button handler
    this.handleBackButton();
  }

  createClone() {
    let iconFileUploadComplete = this.labService.currentSelectedGadget.iconFileUploadComplete
    this.clonedGadgetComplete = JSON.parse(JSON.stringify(this.labService.currentSelectedGadget));
    this.clonedGadgetComplete.iconFileUploadComplete = iconFileUploadComplete;

    let currentRole = this.labService.currentSelectedGadget.currentGadgetUserComplete.gadgetUser.roleId;
    this.canEdit = currentRole <= RoleEnum.ADMIN;

    // Initialize selectedCuratedImage from gadget's curatedIconUrl if available
    if (this.clonedGadgetComplete.gadget?.curatedIconUrl) {
      this.selectedCuratedImage = this.clonedGadgetComplete.gadget.curatedIconUrl;
    }
  }

  ngAfterViewInit() {
    // Set initial selected image
    this.selectedCuratedImage = '../../assets/icon/default_gadget.png';
  }



  getDisplayImage(): string {
    // Prioritize curated icon URL from gadget if available
    if (this.clonedGadgetComplete?.gadget?.curatedIconUrl) {
      return this.clonedGadgetComplete.gadget.curatedIconUrl;
    }
    // Fall back to uploaded image if available
    if (this.clonedGadgetComplete?.iconFileUploadComplete) {
      return this.clonedGadgetComplete.iconFileUploadComplete.srcUrl;
    }
    return this.selectedCuratedImage || '../../assets/icon/default_gadget.png';
  }

  onImageClick() {
    // Only allow image click if editing is enabled
    if (!this.canEdit) {
      console.log('[GadgetSettings] Image click disabled - user cannot edit gadget');
      return;
    }
    // Navigate to second slide when clicking on image
    const swiper: Swiper = this.mainSwiperRef.nativeElement.swiper;
    if (swiper) {
      swiper.slideNext();
    }
  }

  returnToFirstSlide() {
    const swiper: Swiper = this.mainSwiperRef.nativeElement.swiper;
    if (swiper) {
      swiper.slideTo(0);
    }
  }

  onCuratedImageSelected(event: { url: string }) {
    this.selectedCuratedImage = event.url;
    this.clonedGadgetComplete.iconFileUploadComplete = null;
    this.clonedGadgetComplete.gadget.iconFileUploadId = null;
    this.clonedGadgetComplete.gadget.curatedIconUrl = event.url;
  }

  onImageUploaded(fileUploadComplete: FileUploadComplete) {
    // Update the iconFileUploadComplete
    this.clonedGadgetComplete.gadget.iconFileUploadId = fileUploadComplete.fileUpload.id;
    this.clonedGadgetComplete.gadget.curatedIconUrl = null;
    this.clonedGadgetComplete.iconFileUploadComplete = fileUploadComplete;
  }

  deleteGadget() {

    presentAlertConfirm("Delete Gadget", "Are you sure you want to delete this gadget?", this.matDialog).then((result) => {
      if (result) {
        let loadingRef = presentLoading(this.matDialog);
        this.gadgetService.deleteGadget(this.userAuthService, this.labService.currentSelectedGadget.gadget.id).then(() => {
          presentSnackBar("Gadget has been deleted!", SnackBarColor.SUCCESS, this.matSnackBar);
          this.dialogRef.close();
          this.gadgetService.deleteGadgetLocally(this.labService.currentSelectedGadget.gadget.id, this.userAuthService);
        }).finally(() => {
          loadingRef.close();
        })
      }
    })


  }


  updateGadgetSettings() {
    let loadingRef = presentLoading(this.matDialog);

    // Owner dropdown removed, so no need to attach ownerUserId for transfer logic here
    // Ownership transfer is now handled exclusively via the Member List (GadgetUserList component)

    this.gadgetService.updateGadgetSettings(this.userAuthService, this.clonedGadgetComplete).then(() => {
      presentSnackBar("Gadget settings have been updated!", SnackBarColor.SUCCESS, this.matSnackBar);
    }).finally(() => {
      loadingRef.close();
    })
  }

  /**
   * Handle back button press when component is loaded as a dialog
   */
  handleBackButton() {
    console.log('[GadgetSettings] handleBackButton called');

    if (this.platform && this.platform.backButton) {
      console.log('[GadgetSettings] Setting up back button handler with priority:', AndroidBackPriority.DIALOG);
      this.backButtonSub = this.platform.backButton.subscribeWithPriority(AndroidBackPriority.DIALOG, () => {
        console.log('[GadgetSettings] 🎯 BACK BUTTON PRESSED! Closing dialog...');
        this.dialogRef.close();
      });
      console.log('[GadgetSettings] ✅ Back button handler set up successfully');
    } else {
      console.log('[GadgetSettings] Platform or backButton not available');
    }
  }

  /**
   * Clean up back button subscription when component is destroyed
   */
  ngOnDestroy() {
    console.log('[GadgetSettings] ngOnDestroy called, cleaning up back button subscription');
    this.cleanupBackButtonSubscription();
  }

  /**
   * Clean up back button subscription manually
   */
  private cleanupBackButtonSubscription() {
    if (this.backButtonSub) {
      try {
        this.backButtonSub.unsubscribe();
        console.log('[GadgetSettings] ✅ Back button subscription cleaned up');
      } catch (err) {
        console.warn('[GadgetSettings] Error cleaning up back button subscription:', err);
      }
      this.backButtonSub = null;
    }
  }

  /**
   * Public method to cleanup back button subscription before dialog closes
   */
  public cleanupBeforeClose() {
    console.log('[GadgetSettings] cleanupBeforeClose called');
    this.cleanupBackButtonSubscription();
  }
}
